"use strict";

// Vue
import Vue from "vue";
import router from "router";

// API
import { HTTP } from "api/http";
import {
  requestLogin,
  requestCurrentUser,
  requestRegisterUser,
  requestResetPassword,
  requestConfirmPassword,
  requestUpdatePassword,
  requestUpdateUser,
  getDentistLocations,
  requestUpdateDentistLocation,
  requestAddDentistLocation,
  requestRemoveDentistLocation,
} from 'api/auth';

// Services
import { arrayIncludes, arrayLoop } from "services/array";

// Models
import roles from "models/global/roles";

// State
const state = {
  authToken: ``,
  user: {
    profile: {},
  },
  redirect: ``,
  locations: {},
  locationsLoading: true,
  tourType: ``,
};

// Getters
const getters = {
  isAuthenticated: state => !!state.authToken,
  user: state => {
    const user = state.user;

    const firstName = user.first_name || ``;
    const lastName = user.last_name || ``;
    const fullName = `${firstName}${firstName ? ` ` : ``}${lastName}`;

    if (!user.profile) user.profile = {};

    return {
      ...user,
      full_name: fullName,
      locations: state.locations,
      locationsCount: state.locationsLoading ? -1 : Object.keys(state.locations).length,
    };
  },
  userRole: state => {
    const role = state.user.role;
    const object = roles[role] || {};

    return {
      role,
      string: object.name,
      object,
    };
  },
  userIsRole: state => toCheck => toCheck === true || arrayIncludes(toCheck, state.user.role),
  showTour: state => state.tourType,
};

// Mutations
const mutations = {
  SET_TOUR(state, value) {
    state.tourType = value;
  },
  SET_TOKEN(state, payload) {
    state.authToken = payload;
    localStorage.setItem(`auth_token`, payload);

    if (payload) {
      HTTP.defaults.headers.common.Authorization = `Token ${payload}`;
    } else {
      delete HTTP.defaults.headers.common.Authorization;
    }
  },
  SET_USER(state, user) {
    Vue.set(state, `user`, user);
  },
  SET_REDIRECT(state, payload) {
    Vue.set(state, `redirect`, payload || ``);
  },
  SET_LOCATIONS(state, locations) {
    Vue.set(state, `locations`, locations);
  },
  SET_LOCATIONS_LOADING(state, boolean) {
    state.locationsLoading = boolean;
  },
  SET_LOCATION_BY_ID(state, { id, location }) {
    Vue.set(state.locations, id, location);
  },
  REMOVE_LOCATION_BY_ID(state, id) {
    // Vue.set(state.locations, id, undefined);
    const locations = {};

    arrayLoop(state.locations, (locationId, location) => {
      if (id === locationId) return;

      locations[locationId] = location;
    });

    Vue.set(state, `locations`, locations);
  },
  SET_USER_PROFILE_VALUE(state, { key, value }) {
    Vue.set(state.user.profile, key, value);
  },
  RESET_ON_LOGOUT(state) {
    Vue.set(state, `user`, {
      profile: {},
    });
    Vue.set(state, `locations`, {});
    state.locationsLoading = false;
    state.redirect = ``;
  },
};

// Actions
const actions = {
  activateTour({ commit }, tourType) {
    commit(`SET_TOUR`, tourType || ``);
  },
  deactivateTour({ commit }) {
    commit(`SET_TOUR`, ``);
  },
  requestLogout({ dispatch }, args = {}) {
    return new Promise(resolve => {
      const modulesToReset = [
        `auth`,
        `review`,
      ];
      arrayLoop(modulesToReset, (i, module) => {
        dispatch(`${module}/resetOnLogout`, null, {
          root: true,
        });
      });
      setTimeout(
        () => {
          if (!args.noRedirect) {
            router.push({
              name: `home`,
            });
          }

          resolve();
        },
        args.noDelay ? 500 : 0
      );
    });
  },

  requestLogin({ dispatch, state }, model) {
    return new Promise((resolve, reject) => {
      requestLogin(model)
        .then(response => {
          dispatch(`updateUser`, response.data || {});
          resolve(response);
          const path = state.redirect;

          setTimeout(() => {
            router.push(path ? { path } : { name: `dashboard` });
            dispatch(`storeRedirect`, ``);
          }, 500);
        })
        .catch(error => {
          console.error(error);
          reject(error);
        });
    });
  },

  requestResetPassword(context, model) {
    return new Promise((resolve, reject) => {
      requestResetPassword(model)
        .then(response => resolve(response))
        .catch(error => {
          console.error(error);
          reject(error);
        });
    });
  },

  requestConfirmPassword({ rootState, state, dispatch }, model) {
    return new Promise((resolve, reject) => {
      const token = rootState.app.page.params.token;

      if (!token) {
        reject({
          errors: `No token`,
        });
      } else {
        model.token = token;
      }

      requestConfirmPassword(model)
        .then(response => {
          const data = response.data || {};

          dispatch(`updateUser`, data);

          const path = state.redirect;

          Vue.nextTick(() => {
            if (state.redirect) {
              router.push(data.auth_token ? path : {
                name: `login`,
              });
            }

            resolve(response);
          });
        })
        .catch(error => {
          console.error(error);
          reject(error);
        });
    });
  },

  requestUpdatePassword(context, { current_password, new_password }) {
    const model = {
      current_password,
      new_password,
    };
    return requestUpdatePassword(model);
  },

  requestUpdateUser({ dispatch }, model) {
    return new Promise((resolve, reject) => {
      requestUpdateUser(model)
        .then(response => {
          dispatch(`updateUser`, response.data || {});
          resolve(response);
        })
        .catch(error => {
          console.error(error);
          reject(error);
        });
    });
  },

  requestCurrentUser({ state, commit, dispatch }, { auth_token, redirect }) {
    if (!state.auth_token) {
      commit(`SET_TOKEN`, auth_token);
    }

    return new Promise((resolve, reject) => {
      requestCurrentUser()
        .then(response => {
          const data = response.data || {};
          dispatch(`updateUser`, response.data || {});
          resolve(response);

          if (data.role === 2 || data.role ===  3) dispatch(`getDentistLocations`);

          if (redirect) {
            const path = state.redirect;
            router.push(path ? { path } : { name: `Home` });
          }
        })
        .catch(error => {
          console.error(error);
          dispatch(`requestLogout`, {
            noDelay: true,
          });
          reject(error);
        });
    });
  },

  requestRegisterPatient({ dispatch }, model) {
    model.role = 1;

    return new Promise((resolve, reject) => {
      requestRegisterUser(model)
        .then(response => {
          dispatch(`updateUser`, response.data || {});

          const path = state.redirect;
          router.push(path ? { path } : { name: `dashboard` });

          resolve(response);
        })
        .catch(error => {
          console.error(error);
          reject(error);
        });
    });
  },

  requestRegisterDentist({ dispatch }, model) {
    model.role = 2;

    return new Promise((resolve, reject) => {
      requestRegisterUser(model)
        .then(response => {
          dispatch(`updateUser`, response.data || {});
          const path = state.redirect;
          router.push(path ? { path } : { name: `dashboard` });

          resolve(response);
        })
        .catch(error => {
          console.error(error);
          reject(error);
        });
    });
  },

  getDentistLocations({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      getDentistLocations()
        .then(res => {
          const locations = {};
          const data = res.data || {};
          const results = data.results || [];

          arrayLoop(results, (i, location) => (locations[location.id] = location));

          commit(`SET_LOCATIONS`, locations);
          dispatch(`setLocationsLoading`, false);
          resolve(res.data);
        })
        .catch(err => {
          console.warn(`getDentistLocations Err`, err);
          reject(err);
        });
    });
  },

  requestUpdateDentistLocation({ dispatch }, model) {
    return new Promise((resolve, reject) => {
      const action = model.id ? requestUpdateDentistLocation : requestAddDentistLocation;
      action(model)
        .then(response => {
          dispatch(`updateDentistLocation`, response.data || {});
          resolve(response);
        })
        .catch(error => {
          console.error(error);
          reject(error);
        });
    });
  },


  requestRemoveDentistLocation({ dispatch }, id) {
    return new Promise((resolve, reject) => {
      requestRemoveDentistLocation(id)
        .then(response => {
          dispatch(`removeDentistLocation`, id);
          resolve(response);
        })
        .catch(error => {
          console.error(error);
          reject(error);
        });
    });
  },

  // requestRemoveDentistLocation(context, model) {
  //   return requestRemoveDentistLocation(model);
  // },

  removeDentistLocation({ commit }, id) {
    commit(`REMOVE_LOCATION_BY_ID`, id);
  },

  updateDentistLocation({ commit, dispatch }, data) {
    commit(`SET_LOCATION_BY_ID`, {
      id: data.id,
      location: data,
    });
    dispatch(`setLocationsLoading`, false);
  },

  setLocationsLoading({ commit }, loading) {
    commit(`SET_LOCATIONS_LOADING`, loading);
  },

  updateUser({ commit }, data) {
    if (data.auth_token) {
      commit(`SET_TOKEN`, data.auth_token);
    }
    commit(`SET_USER`, data);
  },

  storeRedirect({ commit }, path) {
    commit(`SET_REDIRECT`, path);
  },

  updateUserProfileValue({ commit }, { key, value }) {
    commit(`SET_USER_PROFILE_VALUE`, { key, value });
  },

  resetOnLogout({ commit }) {
    commit(`SET_TOKEN`, ``);
    commit(`RESET_ON_LOGOUT`);
  },
};

// Export
export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};



// WEBPACK FOOTER //
// ./client/store/auth.js